import axios from 'axios';
import { LOGIN_PATH, USER_AUTH_PATH } from './constants/auth';
import { HTTP_STATUS } from './constants/httpStatus';
import { clearLegacyClientStorage } from '@/common/functions/clearLegacyClientStorage';

const AUTH_REFRESH_LOCK = 'crm-auth-refresh';

class Api {
	constructor() {
		this.axios = this.createAxios();
		this.refreshPromise = null;
		this.isEndingSession = false;
		this.useInterceptors();
	}

	createAxios() {
		return axios.create({
			baseURL: import.meta.env.VITE_CRM_URL,
			withCredentials: true,
		});
	}

	hasFreshSession() {
		return this.axios
			.get(USER_AUTH_PATH.ME, { skipAuthRefresh: true })
			.then(() => true)
			.catch(() => false);
	}

	refreshAccessTokenOnThisTab() {
		return this.axios.post(USER_AUTH_PATH.REFRESH_TOKEN, undefined, {
			skipAuthRefresh: true,
		});
	}

	async refreshAccessTokenAcrossTabs() {
		const refreshIfNeeded = async () => {
			const hasSession = await this.hasFreshSession();
			if (hasSession) {
				return;
			}

			return this.refreshAccessTokenOnThisTab();
		};

		if (navigator.locks?.request) {
			return navigator.locks.request(AUTH_REFRESH_LOCK, refreshIfNeeded);
		}

		return refreshIfNeeded();
	}

	refreshAccessToken() {
		if (!this.refreshPromise) {
			this.refreshPromise = this.refreshAccessTokenAcrossTabs().finally(() => {
				this.refreshPromise = null;
			});
		}

		return this.refreshPromise;
	}

	async endSession() {
		if (this.isEndingSession) {
			return;
		}

		this.isEndingSession = true;
		clearLegacyClientStorage(true);

		try {
			await this.axios.post(USER_AUTH_PATH.LOGOUT);
		} catch (error) {
			void error;
		}

		const isAlreadyOnLogin = window.location.pathname === LOGIN_PATH;
		if (isAlreadyOnLogin) {
			this.isEndingSession = false;
			return;
		}

		window.location = LOGIN_PATH;
	}

	useInterceptors() {
		this.axios.interceptors.response.use(
			(response) => response,
			async (error) => {
				const status = error.response?.status;
				const originalRequest = error.config;
				const requestUrl = originalRequest?.url || '';
				const isLoginOrLogout =
					requestUrl.includes(USER_AUTH_PATH.LOGIN) ||
					requestUrl.includes(USER_AUTH_PATH.LOGOUT);
				const isRefreshRequest = requestUrl.includes(
					USER_AUTH_PATH.REFRESH_TOKEN,
				);
				const shouldIgnoreRefresh =
					!originalRequest ||
					originalRequest.skipAuthRefresh ||
					isRefreshRequest;

				if (status !== HTTP_STATUS.UNAUTHORIZED) {
					if (status === HTTP_STATUS.FORBIDDEN) {
						await this.endSession();
						throw error;
					}
					if (error.response?.data?.message) {
						throw new Error(error.response.data.message);
					}

					throw error;
				}

				if (shouldIgnoreRefresh) {
					throw error;
				}

				const shouldEndSession = originalRequest._retry || isLoginOrLogout;
				if (shouldEndSession) {
					await this.endSession();
					throw error;
				}

				originalRequest._retry = true;

				try {
					await this.refreshAccessToken();
					return this.axios.request(originalRequest);
				} catch (refreshError) {
					await this.endSession();
					throw refreshError;
				}
			},
		);
	}
}
const api = new Api();

export default api;
